import React, { useEffect, useState } from "react";
import { useAuth } from "../../auth/FirebaseAuth";
import { getUserById } from "../../models/UserFunctions";
import {
  getOrgById,
  transformOrgsToTable,
} from "../../models/OrgFunctions";
import Icon1 from "../../icons/Icon";
import { queryCollection } from "../../models/DataFunctions";
import Space from "../../layout/Space";
import DataGridGPT2 from "../../tables/DataGridGPT2";
import SearchBarCarnets from "../../textfields/SearchBarCarnets";
import { User } from "../../types/user";
import { Org } from "../../types/org";
import type { User as FirebaseUser } from "firebase/auth";

interface OrgsProps {
  navigateWorkspace: (path: string, org?: Org | null) => void;
  userData?: User;
  orgData?: Org;
}

function Orgs({ navigateWorkspace, userData: propsUserData, orgData: propsOrgData }: OrgsProps) {
  const { currentUser, loading } = useAuth() as {
    currentUser: FirebaseUser | null;
    loading: boolean;
  };
  const [userData, setUserData] = useState<User | null>(propsUserData || null);
  const [orgData, setOrgData] = useState<Org | null>(propsOrgData || null);
  const [searchValue, setSearchValue] = useState("");
  const [rows, setRows] = useState<any[]>([]);
  const [columns, setColumns] = useState<any[]>([]);
  const [filterType, setFilterType] = useState<string | null>(null);
  const [orgs, setOrgs] = useState<Org[]>([]);
  const [options, setOptions] = useState<any[]>([]);

  // Function to navigate to an org by ID
  const gotoOrg = (org: any) => {
    navigateWorkspace(`/admin/org/${org.id}`);
  };

  useEffect(() => {
    // Filter orgs based on search value
    let filteredOrgs = orgs;
    if (searchValue) {
      filteredOrgs = orgs.filter(
        (org) =>
          org.name?.toLowerCase().includes(searchValue.toLowerCase()) ||
          org.email?.toLowerCase().includes(searchValue.toLowerCase())
      );
    }

    const { rows, columns } = transformOrgsToTable(filteredOrgs, filterType as any);
    setRows(rows);
    setColumns(columns);
  }, [orgs, filterType, searchValue]);

  // Function to fetch data for the current user
  const handleFetchData = async () => {
    if (currentUser) {
      // Fetch user data by currentUser's ID
      const user = (await getUserById(currentUser.uid)) as User;
      if (!user) {
        console.error("User not found");
        return;
      }

      // Fetch organization data using the user's org reference
      const org = (await getOrgById(user.org_id)) as Org;
      if (!org) {
        console.error("Org not found");
        return;
      }

      // Fetch all organizations
      const orgs = (await queryCollection("org", [], 10000)) as Org[];

      // Set options for search bar
      setOptions(
        orgs.map((org) => ({
          label: org.name || org.email || "Sans nom",
          value: org.id,
          extra_label: org.email,
        }))
      );

      // Update state
      setUserData(user);
      setOrgData(org);
      setOrgs(orgs);
    }
  };

  const handleSetSearchValue = (value: string) => {
    setSearchValue(value);
  };

  const gotoOrgByName = (option: any) => {
    const org = orgs.find((org) => org.id === option.value);
    if (org) {
      navigateWorkspace(`/admin/org/${org.id}`);
    }
  };

  // Fetch data when the component mounts or when currentUser changes
  useEffect(() => {
    if (currentUser) {
      handleFetchData();
    }
  }, [currentUser]);

  // If the data is still loading or the user is not authenticated, show a loading message
  if (loading || !currentUser || !userData) {
    return <div></div>;
  }

  return (
    <div className="flex flex-col h-full w-full pl-4">
      <div className="flex flex-row w-full h-full">
        <div className="flex flex-col gap-2 h-full w-full overflow-hidden">
          <div className="text-hx font-medium">Organisations</div>

          {/* Search Bar */}
          <SearchBarCarnets
            orgData={orgData || undefined}
            options={options}
            setValue={handleSetSearchValue}
            value={searchValue}
            onAction={gotoOrgByName}
            setSeachType={() => {}}
          />

          <div className="flex flex-row items-center gap-2 w-full">
            <div className="text-h3">
              Affichage: {rows.length} / {orgs.length} organisations
            </div>
          </div>
          <Space y={0} />
          {/* DataGrid displaying the orgs */}
          <div
            style={{ padding: "0px" }}
            className="w-full h-full max-w-c1p4k overflow-y-auto mb-6 pb-6"
          >
            <div className="h-full min-h-6 pb-6">
              <DataGridGPT2
                rows={rows}
                columns={columns}
                onClick={gotoOrg}
                initial_sort_field="name"
                initial_asc="asc"
                orgData={orgData}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Orgs;
